(() => {
  const DEBOUNCE_MS = 500;

  class FsCartDrawer {
    constructor(el) {
      this.el = el;
      this.overlay = el.querySelector('.fs-cart-drawer__overlay');
      this.panel = el.querySelector('.fs-cart-drawer__panel');
      this.body = el.querySelector('[data-fs-cart-body]');
      this.footer = el.querySelector('[data-fs-cart-footer]');
      this.titleEl = el.querySelector('[data-fs-cart-title]');
      this.errorEl = el.querySelector('[data-fs-cart-error]');
      this.activeElement = null;
      this._qtyTimers = new Map();
      this._errorTimer = null;

      this._onKeyUp = (e) => e.key === 'Escape' && this.close();
      this._onOverlayClick = () => this.close();

      this.overlay.addEventListener('click', this._onOverlayClick);
      this.el.addEventListener('keyup', this._onKeyUp);

      this.el.addEventListener('click', (e) => {
        const closeBtn = e.target.closest('[data-fs-cart-close]');
        if (closeBtn) this.close();

        const removeBtn = e.target.closest('[data-fs-cart-remove]');
        if (removeBtn) {
          const key = removeBtn.dataset.fsCartRemove;
          if (key) this.updateItem(key, 0);
        }
      });

      this.el.addEventListener('change', (e) => {
        const input = e.target.closest('[data-fs-cart-qty]');
        if (!input) return;
        const key = input.dataset.fsCartQty;
        const qty = parseInt(input.value, 10);
        if (!isNaN(qty) && qty >= 0) this._scheduleUpdate(key, qty);
      });

      this.el.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-fs-cart-qty-minus], [data-fs-cart-qty-plus]');
        if (!btn) return;
        const key = btn.dataset.fsCartQtyMinus || btn.dataset.fsCartQtyPlus;
        const input = this.el.querySelector(`[data-fs-cart-qty="${CSS.escape(key)}"]`);
        if (!input) return;
        const delta = btn.hasAttribute('data-fs-cart-qty-minus') ? -1 : 1;
        const current = parseInt(input.value, 10) || 0;
        const next = Math.max(0, current + delta);
        input.value = next;
        this._scheduleUpdate(key, next);
      });
    }

    _scheduleUpdate(key, quantity) {
      if (this._qtyTimers.has(key)) clearTimeout(this._qtyTimers.get(key));
      const timer = setTimeout(() => {
        this._qtyTimers.delete(key);
        this.updateItem(key, quantity);
      }, DEBOUNCE_MS);
      this._qtyTimers.set(key, timer);
    }

    open(trigger = null) {
      this.activeElement = trigger || document.activeElement;
      lockBodyScroll();
      this.el.classList.add('is-open');
      document.body.classList.add('fs-cart-drawer-open');
      this.el.removeAttribute('aria-hidden');
      requestAnimationFrame(() => {
        const focusTarget = this.el.querySelector('.fs-cart-drawer__close');
        if (focusTarget) focusTarget.focus();
      });
    }

    close() {
      this.el.classList.remove('is-open');
      this.el.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('fs-cart-drawer-open');
      unlockBodyScroll();
      if (this.activeElement && typeof this.activeElement.focus === 'function') {
        this.activeElement.focus();
      }
    }

    setLoading(state) {
      this.el.classList.toggle('is-updating', state);
    }

    showError(message) {
      if (!this.errorEl) return;
      if (this._errorTimer) clearTimeout(this._errorTimer);
      this.errorEl.textContent = message;
      this.errorEl.classList.add('is-visible');
      this._errorTimer = setTimeout(() => this.hideError(), 4000);
    }

    hideError() {
      if (!this.errorEl) return;
      this.errorEl.classList.remove('is-visible');
    }

    showItemError(key, message) {
      const container = this.body && this.body.querySelector(`[data-fs-cart-item-error="${CSS.escape(key)}"]`);
      if (!container) { this.showError(message); return; }
      const inner = container.querySelector('span') || container;
      inner.textContent = message;
      container.classList.add('is-visible');
      setTimeout(() => container.classList.remove('is-visible'), 4000);
    }

    async updateItem(key, quantity) {
      this.setLoading(true);
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ id: key, quantity }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.description || 'Could not update cart.');
        }

        await this.rerender();
      } catch (err) {
        await this.rerender();
        this.showItemError(key, err.message || 'Something went wrong. Please try again.');
      } finally {
        this.setLoading(false);
      }
    }

    async rerender() {
      try {
        const res = await fetch('/?sections=fs-cart-drawer,cart-icon-bubble');
        if (!res.ok) return;
        const data = await res.json();
        const html = data['fs-cart-drawer'];
        if (!html) return;

        const doc = new DOMParser().parseFromString(html, 'text/html');

        const newBody = doc.querySelector('[data-fs-cart-body]');
        const newFooter = doc.querySelector('[data-fs-cart-footer]');
        const newTitle = doc.querySelector('[data-fs-cart-title]');

        if (this.body && newBody) this.body.innerHTML = newBody.innerHTML;
        if (this.footer && newFooter) this.footer.innerHTML = newFooter.innerHTML;

        const hasItems = !doc.querySelector('.fs-cart-drawer__empty');
        this.el.classList.toggle('has-items', hasItems);
        if (this.titleEl && newTitle) this.titleEl.textContent = newTitle.textContent;

        const bubble = document.querySelector('#cart-icon-bubble');
        const bubbleHtml = data['cart-icon-bubble'];
        if (bubble && bubbleHtml) {
          const bubbleDoc = new DOMParser().parseFromString(bubbleHtml, 'text/html');
          const newBubble = bubbleDoc.querySelector('#cart-icon-bubble');
          if (newBubble) bubble.innerHTML = newBubble.innerHTML;
        }
      } catch {
        // silently fail
      }
    }
  }

  const init = () => {
    const el = document.getElementById('FsCartDrawer');
    if (!el) return;

    const drawer = new FsCartDrawer(el);

    // Wire up all triggers
    document.querySelectorAll('[data-fs-cart-trigger]').forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        drawer.open(trigger);
      });
    });

    // Open drawer after successful add-to-cart
    document.addEventListener('fs:cart:add', async (event) => {
      const { open = true, trigger = null } = event.detail || {};
      await drawer.rerender();
      if (open) drawer.open(trigger);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
